import { Instance, SnapshotOut, types, flow, getRoot } from "mobx-state-tree"
import { RootStoreModel } from ".."
import { OrderModel } from "../order"
import { PaymentModel } from "../payment"
import { ExchangeModel } from "../exchange"
import {
  Event,
  EventNames,
  ExchangeOrderData,
  ExchangeOrderStatus,
  FLAG_REPLY,
  TransferData,
} from "../../proto/nbchat-proto"

export interface WalletInfoProps {
  is_id_verify: boolean
  is_phone_verify: boolean
  pay_pwd: boolean
  credit_score: number
  balance: number
  realName: string
  phone?: string
}

/**
 * 錢包資訊 store
 */
export const WalletStoreModel = types
  .model("WalletStore")
  .props({
    id: types.maybeNull(types.string),
    username: types.maybeNull(types.string),
    credit_score: types.maybeNull(types.number),
    is_id_verify: types.optional(types.boolean, false),
    is_phone_verify: types.optional(types.boolean, false),
    realName: types.maybeNull(types.string),
    pay_pwd: types.maybeNull(types.boolean),
    balance: types.optional(types.number, 0),
    orderList: types.optional(types.array(OrderModel), []),
    paymentList: types.optional(types.array(PaymentModel), []),
    exchangeList: types.optional(types.array(ExchangeModel), []),
    phone: types.maybeNull(types.string),
  })
  .volatile<{
    exchangeOrderData: ExchangeOrderData | false
    isExchangeAlertShowing: boolean
  }>(() => ({
    exchangeOrderData: false,
    isExchangeAlertShowing: false,
  }))
  .views(() => ({
    get authenticated() {
      return ""
    },
  }))
  .actions((self) => {
    const { authStore, apiStore } = getRoot<Instance<typeof RootStoreModel>>(self)

    const setWalletInfo = (deta: WalletInfoProps) => {
      self.is_id_verify = deta.is_id_verify
      self.is_phone_verify = deta.is_phone_verify
      self.credit_score = deta.credit_score
      self.balance = deta.balance
      self.realName = deta.realName
      self.pay_pwd = deta.pay_pwd
      self.phone = deta.phone || null
    }
    const setVerify = () => {
      self.is_id_verify = true
    }
    const setPhoneVerify = () => {
      self.is_phone_verify = true
    }
    const setUserName = (str: string) => {
      self.username = str
    }
    const setPay_pwd = () => {
      self.pay_pwd = true
    }

    const setPaymentList = (deta: any) => {
      self.paymentList = deta
    }
    const setExchangeList = (deta: any[]) => {
      const filterArray = deta.filter((exchange) => {
        return exchange.order_status === ExchangeOrderStatus.PENDING
      })
      self.exchangeList = filterArray as any
    }
    const getExchange = () => {
      return self.exchangeList.find((exchange) => {
        return exchange.order_status === ExchangeOrderStatus.PENDING
      })
    }
    const onWalletBalanceChanged = () => {
      getWalletInfo()
    }
    const clearExchangeAlert = () => {
      self.exchangeOrderData = false
      self.isExchangeAlertShowing = false
    }
    const onExchageOrcerSucceeded = ({ data }: Event<ExchangeOrderData> | any) => {
      const payload = data?.data ? data.data : data
      self.exchangeOrderData = payload
      self.isExchangeAlertShowing = true
    }

    const loadPaymentList = flow(function* () {
      const token = authStore.getToken
      const res = yield apiStore.getPaymentList({ token })
      if (res.code === 200 && res.data != null) {
        setPaymentList(res.data)
        return res.data
      } else {
        return []
      }
    })
    const getExchangeList = flow(function* () {
      const token = authStore.getToken
      const res = yield apiStore.getExchangeList({ token })

      if (res.code === 200 && res.data != null) {
        setExchangeList(res.data)
        return res.data
      } else {
        return []
      }
    })

    const getWalletInfo = flow(function* () {
      const token = authStore.getToken
      const res = yield apiStore.getWalletInfo({ token })
      if (res.code === 200 && res.data) {
        const walletInfo: WalletInfoProps = {
          is_id_verify: false,
          is_phone_verify: false,
          credit_score: 0,
          balance: 0,
          realName: "",
          pay_pwd: false,
          phone: "",
        }
        const data = res.data
        walletInfo.is_id_verify = data.is_id_verify
        walletInfo.is_phone_verify = data.is_phone_verify
        walletInfo.credit_score = data.credit_score
        walletInfo.realName = data.real_name
        walletInfo.pay_pwd = data.pay_pwd
        walletInfo.phone = data.phone
        if (data?.wallet) {
          const wallet = data.wallet
          walletInfo.balance = wallet.balance
        }
        setWalletInfo(walletInfo)
      }
    })

    const getWalletLog = flow(function* (params) {
      const token = authStore.getToken
      const res = yield apiStore.getWalletLog({ token, params })
      return res
    })

    const getTakeOrders = flow(function* (params) {
      const token = authStore.getToken
      const res = yield apiStore.getTakeOrders({ token, params })
      return res
    })

    const getTakeOrdersSold = flow(function* (params) {
      const token = authStore.getToken
      const res = yield apiStore.getTakeOrdersSold({ token, params })
      return res
    })

    const getTakeOrdersDisputed = flow(function* (params) {
      const token = authStore.getToken
      const res = yield apiStore.getTakeOrdersDisputed({ token, params })
      return res
    })

    const publicTransferNew = (data: TransferData, onData, onError) => {
      const { eventStore } = getRoot<Instance<typeof RootStoreModel>>(self)
      const evt = new Event<TransferData>(EventNames.TRANSFER_NEW, data)
      eventStore.publish(evt, {
        flags: FLAG_REPLY,
        onData,
        onError,
      })
    }

    return {
      setVerify,
      setPhoneVerify,
      setUserName,
      setWalletInfo,
      loadPaymentList,
      getWalletInfo,
      getWalletLog,
      getTakeOrders,
      getTakeOrdersSold,
      getTakeOrdersDisputed,
      setPay_pwd,
      publicTransferNew,
      onWalletBalanceChanged,
      getExchangeList,
      getExchange,
      onExchageOrcerSucceeded,
      clearExchangeAlert,
    }
  })

type WalletStoreType = Instance<typeof WalletStoreModel>
export interface WalletStore extends WalletStoreType {}
type WalletStoreSnapshotType = SnapshotOut<typeof WalletStoreModel>
export interface WalletStoreSnapshot extends WalletStoreSnapshotType {}
export const createWalletStoreDefaultModel = () => types.optional(WalletStoreModel, {})
